import { DamageNumber } from '@phaser/objects/DamageNumber.object';
import { sendAppMessage } from '@helpers/mobileApp';
import { defaultText } from '@phaser/helpers/Text';
import { playShot } from '@components/sound/Sound';
import { DEPTH } from '@phaser/data/depth';
import { Game } from '@phaser/scenes/Game';
import * as Shared from '@shared/lib';
import i18next from 'i18next';

export class AttackSystem {
  private _scene: Game = Game.current;
  private _lastShootingPos = -1;

  constructor() {}

  public shoot(data: Shared.PlayerShootMessage) {
    const isSelf = this._scene.id.startsWith(data.playerId);

    const card = this._scene.cards.find(e => e.id === data.id);
    if (!card) return;

    if (isSelf) {
      // All feedback is immediate — no travel delay, no stale-target issues
      card.damage(data.damage);
      new DamageNumber(card.container.x, card.container.y - 20, data.damage);

      const shakeIntensity = Math.min(0.011, 0.0025 + data.damage * 0.00004) / window.devicePixelRatio;
      this._scene.cameras.main.shake(55, shakeIntensity);

      playShot();
      sendAppMessage('vibrate');

      if (!data.remainingBullets) this._showReload();

      // Hitscan tracer: a line that flashes from player to card then fades
      this._showTracer(card.container.x, card.container.y);

      // Impact burst at the card
      this._showImpact(card.container.x, card.container.y);
    } else {
      card.damage(data.damage);
      new DamageNumber(card.container.x, card.container.y, data.damage, 0.5);
    }
  }

  public update(_time: number, _delta: number) {
    this._updateShootingPos();
  }

  private _updateShootingPos() {
    const pos = this._scene.inputSystem.getAttackPosition();
    if (!pos) return;

    const gridPos = this._scene.getGridPos(pos.x, pos.y);
    if (gridPos === this._lastShootingPos) return;

    this._lastShootingPos = gridPos;
    this._scene.multiplayerSystem.changeShootingPosition(gridPos);
  }

  private _showTracer(toX: number, toY: number) {
    const camera = this._scene.cameras.main;
    const fromX = camera.displayWidth / 2 + (Math.random() * 20 - 10);
    const fromY = camera.displayHeight - 10;

    const line = this._scene.add.graphics().setDepth(DEPTH.PROJECTILE);
    line.lineStyle(1.5, 0xFFFF66, 0.7);
    line.beginPath();
    line.moveTo(fromX, fromY);
    line.lineTo(toX, toY);
    line.strokePath();

    this._scene.add.tween({
      targets: line,
      alpha: 0,
      duration: 70,
      ease: 'Power2',
      onComplete: () => line.destroy(),
    });

    // Muzzle flash at player position
    const muzzle = this._scene.add.graphics().setDepth(DEPTH.PROJECTILE);
    muzzle.fillStyle(0xFFFF44, 0.85);
    muzzle.fillCircle(fromX, fromY, 5);

    this._scene.add.tween({
      targets: muzzle,
      alpha: 0,
      scale: 2.5,
      duration: 80,
      ease: 'Power2',
      onComplete: () => muzzle.destroy(),
    });
  }

  private _showImpact(x: number, y: number) {
    // Central flash
    const flash = this._scene.add.image(x, y, 'sprites', 'glow')
      .setDepth(DEPTH.PROJECTILE + 2)
      .setScale(0.18)
      .setTint(0xFFFF88)
      .setAlpha(0.9);

    this._scene.add.tween({
      targets: flash,
      scale: 0.04,
      alpha: 0,
      duration: 120,
      ease: 'Power2',
      onComplete: () => flash.destroy(),
    });

    // Radial sparks
    const sparkCount = 5;
    for (let i = 0; i < sparkCount; i++) {
      const angle = (i / sparkCount) * Math.PI * 2 + Math.random() * 0.5;
      const len = 8 + Math.random() * 12;

      const spark = this._scene.add.rectangle(
        x + Math.cos(angle) * 3,
        y + Math.sin(angle) * 3,
        len, 1.5,
        i % 2 === 0 ? 0xFFFF44 : 0xFF8800,
      )
        .setDepth(DEPTH.PROJECTILE + 1)
        .setRotation(angle)
        .setOrigin(0, 0.5);

      this._scene.add.tween({
        targets: spark,
        x: x + Math.cos(angle) * (len + 12),
        y: y + Math.sin(angle) * (len + 12),
        alpha: 0,
        scaleX: 0.1,
        duration: 160,
        ease: 'Power2',
        onComplete: () => spark.destroy(),
      });
    }
  }

  private _showReload() {
    const camera = this._scene.cameras.main;
    const x = camera.displayWidth / 2;
    const y = camera.displayHeight - 50;

    const text = this._scene.add.text(x, y, i18next.t('Game.reloading'), {
      ...defaultText,
      fontSize: '20px',
      color: '#ffaa00',
    })
      .setOrigin(0.5)
      .setDepth(DEPTH.IMPACT);

    this._scene.add.tween({
      onComplete: () => text.destroy(),
      targets: text,
      duration: 900,
      y: y - 50,
      alpha: 0,
      scale: { from: 0.6, to: 1.1 },
      ease: 'Power2',
    });
  }
}
